import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge } from './ui/badge';
import {
  kycDocumentTypes,
  getDocumentAcceptAttribute,
  getDocumentAcceptedFormatsLabel,
  getDocumentUploadHint,
} from '../lib/utils';
import {
  createObjectUrl,
  resolveOutputMimeType,
  revokeObjectUrl,
  shouldCropDocumentUpload,
} from '../lib/imageCrop';
import ImageCropper from './ImageCropper';
import { FileText, Loader2, Upload } from 'lucide-react';

/**
 * Partner registration KYC document upload step.
 * Business Portrait (photo) opens the image cropper before upload; PDFs and other docs upload as-is.
 */
export default function KycDocumentUploadStep({
  documents,
  uploadDocument,
  uploadingDocType,
}) {
  const [cropSession, setCropSession] = useState(null);
  const cropUrlRef = useRef(null);

  const closeCropSession = useCallback(() => {
    revokeObjectUrl(cropUrlRef.current);
    cropUrlRef.current = null;
    setCropSession(null);
  }, []);

  useEffect(() => () => {
    revokeObjectUrl(cropUrlRef.current);
    cropUrlRef.current = null;
  }, []);

  const handleFileSelected = async (docType, file, inputEl) => {
    if (!file) return;
    if (shouldCropDocumentUpload(docType, file)) {
      closeCropSession();
      const imageSrc = createObjectUrl(file);
      cropUrlRef.current = imageSrc;
      setCropSession({
        docType,
        imageSrc,
        fileName: file.name,
        mimeType: resolveOutputMimeType(file),
      });
      if (inputEl) inputEl.value = '';
      return;
    }
    await uploadDocument(docType, file);
    if (inputEl) inputEl.value = '';
  };

  const handleCropApply = async (croppedFile) => {
    const docType = cropSession?.docType;
    closeCropSession();
    if (docType && croppedFile) {
      await uploadDocument(docType, croppedFile);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900">Upload KYC Documents</h2>
        <p className="text-sm text-slate-500 mt-1">Upload your identity and business verification documents</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {kycDocumentTypes.map((dt) => {
          const uploaded = documents.find((d) => d.document_type === dt.value);
          const isUploading = uploadingDocType === dt.value;
          const hint = getDocumentUploadHint(dt.value);
          return (
            <div
              key={dt.value}
              className={`border rounded-2xl p-5 transition-shadow duration-200 ${
                uploaded
                  ? 'border-emerald-200 bg-emerald-50/30 shadow-sm'
                  : 'border-dashed border-slate-300 hover:border-slate-400'
              }`}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">{dt.label}</span>
                  {dt.required ? (
                    <span className="text-[10px] text-red-500 font-medium">*</span>
                  ) : (
                    <span className="text-[10px] text-slate-400">(optional)</span>
                  )}
                </div>
                {uploaded && (
                  <Badge
                    className={`${
                      uploaded.status === 'pending'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    } border text-[10px] shrink-0`}
                  >
                    {uploaded.status === 'pending'
                      ? 'Pending for Review'
                      : uploaded.status.charAt(0).toUpperCase() + uploaded.status.slice(1)}
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mb-2 leading-snug">
                Formats: {getDocumentAcceptedFormatsLabel(dt.value)}
                {hint ? ` — ${hint}` : ''}
              </p>
              {uploaded ? (
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-slate-600 truncate">{uploaded.document_name}</span>
                </div>
              ) : null}
              <label className="mt-2 flex items-center gap-2 text-sm text-[#0066CC] cursor-pointer hover:underline font-medium">
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {uploaded ? 'Replace' : 'Upload'}
                  </>
                )}
                <input
                  type="file"
                  className="hidden"
                  accept={getDocumentAcceptAttribute(dt.value)}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    handleFileSelected(dt.value, file, e.target);
                  }}
                  disabled={!!uploadingDocType || !!cropSession}
                />
              </label>
            </div>
          );
        })}
      </div>

      <ImageCropper
        open={!!cropSession}
        imageSrc={cropSession?.imageSrc || null}
        fileName={cropSession?.fileName}
        mimeType={cropSession?.mimeType}
        title="Crop Business Portrait"
        onCancel={closeCropSession}
        onApply={handleCropApply}
      />
    </div>
  );
}

KycDocumentUploadStep.propTypes = {
  documents: PropTypes.array.isRequired,
  uploadDocument: PropTypes.func.isRequired,
  uploadingDocType: PropTypes.string,
};
